import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { API } from '../lib/api';

const SESSION_KEY = 'cf_chat_session';

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hey — I'm Forge, your launch co-pilot. Ask me anything about creating your token, gas, LP, or Base Sepolia." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const s = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, s);
    return s;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error('Bad response');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // Read the SSE stream and append text deltas to the last assistant message
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n\n');
        buf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          try {
            const j = JSON.parse(line.slice(5).trim());
            if (j.t) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === 'assistant') {
                  copy[copy.length - 1] = { ...last, content: last.content + j.t };
                }
                return copy;
              });
            } else if (j.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', content: `Error: ${j.error}` };
                return copy;
              });
            }
          } catch (_) { /* ignore malformed line */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        if (copy[copy.length - 1]?.role === 'assistant' && !copy[copy.length - 1].content) {
          copy[copy.length - 1] = { role: 'assistant', content: 'Sorry — chat is offline. Try again in a moment.' };
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-br from-[#7DF9FF] to-[#A78BFA] text-[#04121A] font-display font-semibold shadow-[0_10px_30px_rgba(125,249,255,0.4)] hover:scale-105 transition-transform"
        >
          <Sparkles className="w-4 h-4" /> Ask Forge
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-50 w-[min(94vw,380px)] h-[min(70vh,540px)] rounded-3xl overflow-hidden flex flex-col bg-[#0B0A2A]/95 backdrop-blur-2xl border border-[#7DF9FF]/30 shadow-[0_20px_60px_rgba(125,249,255,0.25)]"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#7DF9FF]/10 via-[#A78BFA]/10 to-[#F472B6]/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7DF9FF] to-[#A78BFA] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#04121A]" />
              </div>
              <div>
                <p className="font-display font-semibold text-sm">Forge</p>
                <p className="text-[10px] text-forge-muted">your launch co-pilot · online</p>
              </div>
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="text-forge-muted hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-gradient-to-br from-[#7DF9FF] to-[#A78BFA] text-[#04121A] font-medium rounded-br-md'
                      : 'bg-white/[0.06] text-white border border-white/5 rounded-bl-md'
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? <span className="text-forge-muted italic">thinking…</span> : '')}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 p-3 border-t border-white/10 bg-white/[0.02]"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about tokens, gas, LP…"
              className="cf-input !py-2 !text-sm !rounded-full"
              disabled={busy}
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={busy || !input.trim()}
              className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#7DF9FF] to-[#A78BFA] text-[#04121A] flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
              aria-label="send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
